var _excluded = ["input", "meta", "options", "className", "checkboxInputProps"];
function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React from 'react';
import PropTypes from 'prop-types';
import Checkbox from './checkbox';
import { checkboxGroupPropTypes, fieldOptionsType, omitLabelProps, replaceEmptyStringValue, convertNameToLabel } from '../helpers';
import { LabeledField } from '../labels';
import { addToArray, removeFromArray, serializeOptions, compose } from '../../utils';

/**
 *
 * A group of checkboxes that can be used in a `redux-form`-controlled form.
 *
 * The value of each checkbox is specified via the `options` prop. This prop can either be:
 * - An array of strings
 * - An array of numbers
 * - An array of key-value pairs: `{ key, value }`
 *
 * The value of the entire `CheckboxGroup` component is an **array** containing the values of the selected checkboxes (converted to strings).
 * Clicking an unselected checkbox adds its value to this array, and clicking a selected checkbox removes its value from this array.
 *
 * @name CheckboxGroup
 * @type Function
 * @param {Object} input - A `redux-form` [input](http://redux-form.com/6.5.0/docs/api/Field.md/#input-props) object
 * @param {Object} meta - A `redux-form` [meta](http://redux-form.com/6.5.0/docs/api/Field.md/#meta-props) object
 * @param {Array} options - An array of checkbox values (strings, numbers, or key-value pairs)
 * @param {Object} [checkboxInputProps={}] - An object of key-value pairs representing props to pass down to all checkbox inputs
 * @example
 *
 * function TodoForm ({ handleSubmit, pristine, invalid, submitting }) {
 *   return (
 *     <form onSubmit={ handleSubmit }>
 *       <Field
 *          name="completedTodos"
 *          component={ CheckboxGroup }
 *          options={[
 *            'Eat breakfast',
 *            'Respond to emails',
 *            'Take out the trash',
 *          ]}
 *       />
 *       <SubmitButton {...{ pristine, invalid, submitting }}>
 *         Submit
 *       </SubmitButton>
 *     </form>
 *   )
 * }
 *
 * export default TodoForm
 *
 * @example
 * function TodoForm ({ handleSubmit, pristine, invalid, submitting }) {
 *   return (
 *     <form onSubmit={ handleSubmit }>
 *       <Field
 *          name="completedTodos"
 *          component={ CheckboxGroup }
 *          options={[
 *            'Eat breakfast',
 *            'Respond to emails',
 *            'Take out the trash',
 *          ]}
 *          checkboxInputProps={{
 *            className: 'checkbox-input--secondary',
 *          }}
 *       />
 *       <SubmitButton {...{ pristine, invalid, submitting }}>
 *         Submit
 *       </SubmitButton>
 *     </form>
 *   )
 * }
 *
 * export default TodoForm
 */

var propTypes = _objectSpread(_objectSpread({}, checkboxGroupPropTypes), {}, {
  className: PropTypes.string,
  checkboxInputProps: PropTypes.object,
  options: fieldOptionsType
});
var defaultProps = {
  className: 'CheckboxGroup',
  checkboxInputProps: {},
  options: []
};
function CheckboxGroupLegend(_ref) {
  var name = _ref.name,
    label = _ref.label;
  if (label === false) return null;
  return /*#__PURE__*/React.createElement("legend", null, label || convertNameToLabel(name));
}
function CheckboxGroup(props) {
  var _omitLabelProps = omitLabelProps(props),
    _omitLabelProps$input = _omitLabelProps.input,
    value = _omitLabelProps$input.value,
    onChange = _omitLabelProps$input.onChange,
    name = _omitLabelProps$input.name,
    meta = _omitLabelProps.meta,
    options = _omitLabelProps.options,
    className = _omitLabelProps.className,
    checkboxInputProps = _omitLabelProps.checkboxInputProps,
    rest = _objectWithoutProperties(_omitLabelProps, _excluded);
  var optionObjects = serializeOptions(options);
  // Build change handler
  var handleChange = function handleChange(option) {
    return function (checked) {
      // Add or remove option value from array of values, depending on whether it's checked
      var newValueArray = checked ? addToArray([option.value], value) : removeFromArray([option.value], value);
      return onChange(newValueArray);
    };
  };
  return /*#__PURE__*/React.createElement(LabeledField, _extends({
    className: className,
    labelComponent: CheckboxGroupLegend
  }, props), optionObjects.map(function (option, i) {
    return /*#__PURE__*/React.createElement(Checkbox // eslint-disable-line react/jsx-key
    , _objectSpread(_objectSpread({
      key: i,
      input: {
        name: "".concat(name, ".").concat(option.value),
        value: value.includes(option.value),
        onChange: handleChange(option)
      },
      meta: {},
      label: option.key
    }, rest), checkboxInputProps));
  }));
}
CheckboxGroup.propTypes = propTypes;
CheckboxGroup.defaultProps = defaultProps;
export default compose(replaceEmptyStringValue([]))(CheckboxGroup);